import {Inject, Injectable} from '@angular/core';
import {OAuthEvent, OAuthService} from 'angular-oauth2-oidc';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import {environment, CustomEnvironment, setApiOverrides } from '../../environments/environment';
import {catchError, tap} from 'rxjs/operators';
import {HttpErrorHandlerService, HandleError} from './http-error-handling.service';
import {forkJoin, Observable} from 'rxjs';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Authorization': 'my-auth-token'
  })
};

@Injectable()
export class CalendarService {
  private handleError: HandleError;

  constructor(private oauthService: OAuthService,
              private http: HttpClient,
              httpErrorHandler: HttpErrorHandlerService,
              @Inject(CustomEnvironment) customEnvironment: any) {
    setApiOverrides(customEnvironment);

    this.oauthService.events.subscribe((event: OAuthEvent) => {
      const tokenUpdatedEvents: any = ['token_refreshed', 'token_received', 'silently_refreshed'];
      if (tokenUpdatedEvents.includes(event.type)) {
        httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
      }
    });

    httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
    this.handleError = httpErrorHandler.createHandleError('CalendarService');
  }

  getCalendars(): Observable<any> {
    return this.http.get(`${environment.api.calendar}/calendars`, httpOptions);
  }

  getCalendar(id: string): Observable<any> {
    return this.http.get(`${environment.api.calendar}/calendars/${id}`, httpOptions);
  }

  getEvents(): Observable<any> {
    return this.http.get(`${environment.api.calendar}/events`, httpOptions);
  }

  getEvent(id: number): Observable<any> {
    return this.http.get(`${environment.api.calendar}/events/${id}`, httpOptions);
  }

  createEvent(data: any): Observable<any> {
    return this.http.post(`${environment.api.calendar}/events`, data, httpOptions);
  }

  updateEvent(id: number, data: any): Observable<any> {
    return this.http.put(`${environment.api.calendar}/events/${id}`, data, httpOptions);
  }

  deleteEvent(id: number): Observable<any> {
    return this.http.delete(`${environment.api.calendar}/events/${id}`, httpOptions);
  }

  /**
   *
   * @param calendarId
   * @param data
   * @param createJob -- Create job - triggers create sales-appointment creation hooks
   */
  createBooking(calendarId: string, data: any, createJob = true): Observable<any> {
    data.createJob = createJob;
    return this.http.post(`${environment.api.calendar}/calendars/${calendarId}/events`, data, httpOptions);
  }

  /**
   *
   * @param calendarId
   * @param eventId
   * @param data
   * @param createJob -- Create job - triggers create sales-appointment creation hooks
   */
  updateBooking(calendarId: string, eventId: number, data: any, createJob = true): Observable<any> {
    data.createJob = createJob;
    return this.http.put(`${environment.api.calendar}/calendars/${calendarId}/events/${eventId}`, data, httpOptions);
  }

  cancelBooking(calendarId: string, eventId: number, data: any): Observable<any> {
    return this.http.post(`${environment.api.calendar}/calendars/${calendarId}/events/${eventId}/cancel`, data, httpOptions);
  }

  getAvailability(data: any): Observable<any> {
    return this.http.post(`${environment.api.calendar}/availability`, data, httpOptions);
  }

  getO365Availability(data: any): Observable<any> {
    return this.http.post(`${environment.api.calendar}/o365-availability`, data, httpOptions);
  }

  searchCalendar(query: string): Observable<any> {
    return this.http.get(`${environment.api.calendar}/calendars?query=${query}`, httpOptions);
  }

  searchEvents(query: string): Observable<any> {
    return this.http.get(`${environment.api.calendar}/events?${query}`, httpOptions);
  }

  bulkReassign(calendar, data): Observable<any> {
    return this.http.post(`${environment.api.calendar}/calendars/${calendar}/events/bulk-reassign`, data, httpOptions)
  }
}
